import React from 'react'
import { ComponentMap, ParsedContentItem, ContentData } from './types'
import { parseTipContent } from './tip'
import { parseCardContent } from './card'
import { parseBoxesContent } from './boxes'
import { parseSchemaContent } from './schema'

/**
 * 在服务端使用 jsdom 解析 XML
 * 注意：通过 eval('require') 避免在浏览器打包阶段引入 jsdom 及其 Node 依赖
 */
function parseXmlInServer(xmlString: string): Document {
  // 这里只会在 Node 环境（SSR / build-html）下执行
  // 使用 eval('require') 避免 webpack 在浏览器构建时解析 jsdom 依赖
  // eslint-disable-next-line no-eval, @typescript-eslint/no-implied-eval
  const nodeRequire = eval('require') as NodeRequire
  // eslint-disable-next-line @typescript-eslint/no-var-requires
  const { JSDOM } = nodeRequire('jsdom')
  const dom = new JSDOM(xmlString, {
    contentType: 'text/xml',
  })
  return dom.window.document
}

/**
 * 解析 XML 字符串为 Document 对象
 * 在客户端使用原生 DOMParser，在服务端使用 jsdom
 */
function parseXmlString(xmlString: string): Document {
  if (typeof window !== 'undefined' && window.DOMParser) {
    // 客户端环境，使用原生 DOMParser
    const parser = new window.DOMParser()
    return parser.parseFromString(xmlString, 'text/xml')
  } else {
    // 服务端环境，使用 jsdom
    return parseXmlInServer(xmlString)
  }
}

/**
 * 解析 HTML 属性（从 <Attribute> 子元素中提取）
 * 支持格式：
 * <Attribute>
 *   <ID>123</ID>
 *   <Class>my-class</Class>
 *   <Style>color: red;</Style>
 * </Attribute>
 *
 * @param element - XML 元素节点
 * @returns HTML 属性对象（如 { id: '123', className: 'my-class', style: 'color: red;' }）
 */
export function parseHtmlAttributes(element: Element): Record<string, string> {
  const attributes: Record<string, string> = {}
  const attributeElement = element.querySelector('Attribute')
  if (!attributeElement) {
    return attributes
  }

  // 遍历 <Attribute> 下的所有子元素
  const children = attributeElement.children
  for (let i = 0; i < children.length; i++) {
    const child = children[i]
    const tagName = child.tagName // 可能是 ID, Class, Style 等首字母大写
    const attrName = tagName.toLowerCase() // 转换为小写用于比较
    const attrValue = child.textContent?.trim() || child.innerHTML.trim() || ''

    if (attrValue) {
      // 将属性名转换为 React 支持的格式
      // ID -> id, Class -> className, Style -> style, DataXxx -> dataXxx
      let reactAttrName: string
      if (attrName === 'id') {
        reactAttrName = 'id'
      } else if (attrName === 'class') {
        reactAttrName = 'className'
      } else if (tagName.toLowerCase().startsWith('data-')) {
        reactAttrName = tagName.toLowerCase()
      } else if (tagName.toLowerCase().startsWith('data')) {
        // DataXxx -> dataXxx
        reactAttrName = tagName.charAt(0).toLowerCase() + tagName.slice(1)
      } else {
        // 其他属性转换为小写
        reactAttrName = tagName.toLowerCase()
      }

      attributes[reactAttrName] = attrValue
    }
  }

  return attributes
}

/**
 * 解析子元素的内容和属性
 * 支持格式：
 * <Title>
 *   <Attribute>
 *     <ID>title-123</ID>
 *     <Class>title-class</Class>
 *   </Attribute>
 *   标题内容
 * </Title>
 *
 * @param element - 父元素节点
 * @param childTagName - 子元素标签名（如 'Title', 'Content'）
 * @returns 包含内容和属性的对象
 */
export function parseChildElementWithAttributes(
  element: Element,
  childTagName: string,
): { value: string; attributes: Record<string, string> } {
  const childElement = element.querySelector(childTagName)
  if (!childElement) {
    return { value: '', attributes: {} }
  }

  // 提取属性
  const attributes = parseHtmlAttributes(childElement)

  // 提取内容（排除 Attribute 子元素）
  const clone = childElement.cloneNode(true) as Element
  const attributeInClone = clone.querySelector('Attribute')
  if (attributeInClone) {
    attributeInClone.remove()
  }
  const value = clone.innerHTML.trim() || clone.textContent?.trim() || ''

  return { value, attributes }
}

/**
 * 根据类型解析 XML 内容
 *
 * @param type - 标记类型（如 'tip', 'card'）
 * @param xmlElement - XML 元素节点
 * @returns 解析后的数据对象
 */
function parseContentByType(type: string, xmlElement: Element): ContentData {
  switch (type) {
    case 'tip':
      return parseTipContent(xmlElement)
    case 'card':
      return parseCardContent(xmlElement)
    case 'boxes':
      return parseBoxesContent(xmlElement)
    case 'schema':
      return parseSchemaContent(xmlElement)
    default:
      // 默认使用 tip 格式解析
      return parseTipContent(xmlElement)
  }
}

/**
 * 预处理富文本编辑器输出的内容
 * 1. 解码 HTML 实体（&lt; -> <, &gt; -> >）
 * 2. 移除多余的 div 包装，但保留其他 HTML 标签（如 ul/li 等）
 * 3. 让 XML 自定义标签（Card/Tip/Boxes 等）恢复为真正的标签，便于后续解析
 *
 * @param htmlContent - 原始 HTML 内容
 * @returns 预处理后的内容
 */
function preprocessEditorContent(htmlContent: string): string {
  let result = htmlContent

  // 1. 去掉所有 div 包装，只保留内部内容（避免把 XML 标签切碎）
  // 例如：<div>&lt;Card&gt;</div> -> &lt;Card&gt;
  result = result.replace(/<\/?div[^>]*>/gi, '')

  // 2. 全局解码常见 HTML 实体，让 &lt;Card&gt; 还原为 <Card>
  result = result
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&nbsp;/g, ' ')

  // 3. 规范自闭合标签，避免 XML 解析错误
  // 将 <br> 或 <br > 转为 <br/>
  result = result.replace(/<br(\s*)>/gi, '<br$1/>')

  // 4. 简单清理多余空白
  result = result.trim()

  return result
}

/**
 * 判断是否需要对内容做预处理（是否包含 HTML 实体形式的标签）
 */
function shouldPreprocessEditorContent(htmlContent: string): boolean {
  // 只在包含常见的转义实体时才进行预处理
  return /&(lt|gt|amp|nbsp|quot|#39);/i.test(htmlContent)
}

/**
 * 为只有一个单元格（td/th）的 table 添加 single class，方便在样式中单独处理
 */
function addSingleClassToSingleCellTables(htmlContent: string): string {
  if (!htmlContent || htmlContent.indexOf('<table') === -1) return htmlContent

  return htmlContent.replace(/<table\b[^>]*>[\s\S]*?<\/table>/gi, (tableHtml) => {
    const cellMatches = tableHtml.match(/<(td|th)\b/gi)

    // 只有一个单元格时才处理
    if (!cellMatches || cellMatches.length !== 1) return tableHtml

    // 已经包含 single class 时不重复添加
    if (/class\s*=\s*['"][^'"]*\bsingle\b[^'"]*['"]/i.test(tableHtml)) {
      return tableHtml
    }

    // 已经有 class 属性时，追加 single
    if (/class\s*=\s*['"][^'"]*['"]/i.test(tableHtml)) {
      return tableHtml.replace(
        /(class\s*=\s*['"])([^'"]*)(['"])/i,
        (_, prefix: string, classNames: string, suffix: string) =>
          `${prefix}${classNames} single${suffix}`,
      )
    }

    // 没有 class 属性时，新增 class="single"
    return tableHtml.replace(
      /<table\b([^>]*)>/i,
      (_, attrs: string) => `<table class="single"${attrs}>`,
    )
  })
}

/**
 * 解析 HTML 内容中的 XML 自定义标记（如 <Tip>...</Tip>）
 * 返回一个数组，包含普通 HTML 字符串和 React 组件
 *
 * @param htmlContent - 原始 HTML 内容
 * @param componentMap - 组件映射对象，key 为标记类型（小写），value 为 React 组件
 * @returns 包含字符串和 React 元素的混合数组
 */
export function parseContentWithComponents(
  htmlContent: string,
  componentMap: ComponentMap = {},
): ParsedContentItem[] {
  if (!htmlContent) return []

  // 仅在需要时预处理编辑器内容：解码 HTML 实体、移除多余 div 等
  const contentToParse = shouldPreprocessEditorContent(htmlContent)
    ? preprocessEditorContent(htmlContent)
    : htmlContent

  // 对只有一个单元格的 table 增加 single 类名，便于样式控制
  const contentWithSingleTableClass = addSingleClassToSingleCellTables(contentToParse)

  const result: ParsedContentItem[] = []

  try {
    // 遍历内容字符串，使用正则查找自定义 XML 标签
    let lastProcessedIndex = 0

    // 使用正则表达式找到所有 XML 标签的位置，以便保留原始 HTML 内容
    const xmlTagPattern = /<(\w+)(?:\s[^>]*)?>([\s\S]*?)<\/\1>/g
    let match: RegExpExecArray | null = xmlTagPattern.exec(contentWithSingleTableClass)
    const xmlMatches: Array<{
      fullMatch: string
      type: string
      startIndex: number
      endIndex: number
      content: string
    }> = []

    // 收集所有匹配的 XML 标签（仅支持首字母大写）
    while (match !== null) {
      const [fullMatch, type, content] = match
      const startIndex = match.index
      const endIndex = startIndex + fullMatch.length

      // 检查标签是否首字母大写
      const isCapitalized =
        type.charAt(0) === type.charAt(0).toUpperCase() &&
        type.charAt(0) !== type.charAt(0).toLowerCase()
      if (!isCapitalized) {
        match = xmlTagPattern.exec(contentWithSingleTableClass)
        continue
      }

      // 将标签名转换为小写以匹配 componentMap（componentMap 的 key 是小写的）
      const typeLower = type.toLowerCase()

      // 检查是否是组件映射中的类型
      if (componentMap[typeLower]) {
        xmlMatches.push({
          fullMatch,
          type: typeLower,
          startIndex,
          endIndex,
          content,
        })
      }

      match = xmlTagPattern.exec(contentWithSingleTableClass)
    }

    // 如果没有找到任何匹配的 XML 标签，返回原始内容
    if (xmlMatches.length === 0) {
      return [contentToParse]
    }

    // 处理每个匹配的 XML 标签
    for (let i = 0; i < xmlMatches.length; i++) {
      const xmlMatch = xmlMatches[i]

      // 添加标签前的普通 HTML 内容
      if (xmlMatch.startIndex > lastProcessedIndex) {
        const beforeContent = contentWithSingleTableClass
          .substring(lastProcessedIndex, xmlMatch.startIndex)
          .trim()
        if (beforeContent) {
          result.push(beforeContent)
        }
      }

      // 解析 XML 内容
      try {
        const xmlWrapper = `<root>${xmlMatch.fullMatch}</root>`
        const xmlDoc = parseXmlString(xmlWrapper)

        // 检查解析错误（客户端 DOMParser 会在解析失败时创建 parsererror 元素）
        const parseError = xmlDoc.querySelector('parsererror')
        if (parseError) {
          // 解析失败，保留原始内容
          result.push(xmlMatch.fullMatch)
        } else {
          const element = xmlDoc.documentElement?.firstElementChild
          if (element) {
            const Component = componentMap[xmlMatch.type]
            if (Component) {
              const parsedData = parseContentByType(xmlMatch.type, element)
              // 解析 HTML 属性（从 <Attribute> 子元素中提取）
              const htmlAttributes = parseHtmlAttributes(element)
              result.push(
                React.createElement(Component, {
                  key: `component-${xmlMatch.type}-${xmlMatch.startIndex}`,
                  ...parsedData,
                  ...htmlAttributes,
                }),
              )
            } else {
              result.push(xmlMatch.fullMatch)
            }
          } else {
            result.push(xmlMatch.fullMatch)
          }
        }
      } catch (error) {
        // 解析出错，保留原始内容
        console.error('XML parsing error:', error)
        result.push(xmlMatch.fullMatch)
      }

      lastProcessedIndex = xmlMatch.endIndex
    }

    // 添加最后剩余的普通 HTML 内容
    if (lastProcessedIndex < contentWithSingleTableClass.length) {
      const remainingContent = contentWithSingleTableClass.substring(lastProcessedIndex).trim()
      if (remainingContent) {
        result.push(remainingContent)
      }
    }
  } catch (error) {
    // 如果 XML 解析完全失败，返回原始/预处理后的内容
    return [contentWithSingleTableClass]
  }

  // 如果没有匹配到任何标记，返回预处理后的内容
  if (result.length === 0) {
    return [contentWithSingleTableClass]
  }

  return result
}

// 导出类型
export type {
  ComponentMap,
  ParsedContentItem,
  TipContentData,
  CardContentData,
  BoxesContentData,
  BoxItem,
  SchemaContentData,
} from './types'

// 导出解析函数，方便外部使用
export { parseTipContent } from './tip'
export { parseCardContent } from './card'
export { parseBoxesContent } from './boxes'
export { parseSchemaContent } from './schema'
